import React, { useState, useEffect } from 'react'
import {
  Plus,
  Search,
  Filter,
  Edit2,
  Eye,
  Trash2,
  Calendar,
  Users,
  Clock,
  AlertCircle
} from 'lucide-react'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Pagination from '@/components/ui/Pagination'
import { formatDate, cn } from '@/lib/utils'
import {
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
} from '@/services/calendar'
import EventForm from '@/components/calendar/EventForm'
import SubscribersModal from '@/components/calendar/SubscribersModal'

// Calendar Event interface
interface CalendarEvent {
  id: string
  title: string
  description?: string
  startDate: string
  endDate?: string
  type: string
  priority: string
  isActive: boolean
  isGlobal: boolean
  metadata?: Record<string, any>
  createdAt: string
  updatedAt: string
  subscriberCount?: number
}

// Priority badge colors
const priorityColors: Record<string, string> = {
  urgent: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-green-100 text-green-800',
}

// Type badge colors
const typeColors: Record<string, string> = {
  entrance_exam: 'bg-purple-100 text-purple-800',
  application_deadline: 'bg-red-100 text-red-800',
  admission_result: 'bg-blue-100 text-blue-800',
  counseling_session: 'bg-teal-100 text-teal-800',
  scholarship_deadline: 'bg-amber-100 text-amber-800',
  career_fair: 'bg-indigo-100 text-indigo-800',
  college_event: 'bg-pink-100 text-pink-800',
  exam_result: 'bg-cyan-100 text-cyan-800',
  registration_deadline: 'bg-rose-100 text-rose-800',
  workshop: 'bg-emerald-100 text-emerald-800',
  seminar: 'bg-violet-100 text-violet-800',
  interview: 'bg-sky-100 text-sky-800',
  general: 'bg-gray-100 text-gray-800',
}

const CalendarManagement: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterPriority, setFilterPriority] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalEvents, setTotalEvents] = useState(0)
  const [error, setError] = useState('')

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showSubscribersModal, setShowSubscribersModal] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchEvents = async (page = 1, search = '', type = 'all', priority = 'all', status = 'all') => {
    try {
      setLoading(true)
      const params: any = {
        page,
        limit: 10,
      }

      if (search) params.search = search
      if (type !== 'all') params.type = type
      if (priority !== 'all') params.priority = priority
      if (status !== 'all') params.isActive = status === 'active'

      const response = await getEvents(params)

      // Handle both array and paginated response
      if (Array.isArray(response)) {
        setEvents(response)
        setTotalPages(1)
        setTotalEvents(response.length)
      } else {
        setEvents(response.items || response.data || [])
        setTotalPages(response.totalPages || 1)
        setTotalEvents(response.total ?? 0)
      }
      setError('')
    } catch (error) {
      console.error('Failed to fetch events:', error)
      setError(`Failed to load events: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents(currentPage, searchTerm, filterType, filterPriority, filterStatus)
  }, [currentPage, searchTerm, filterType, filterPriority, filterStatus])

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleAddEvent = () => {
    setSelectedEvent(null)
    setShowAddModal(true)
  }

  const handleEditEvent = (event: CalendarEvent) => {
    setSelectedEvent(event)
    setShowEditModal(true)
  }

  const handleViewEvent = (event: CalendarEvent) => {
    setSelectedEvent(event)
    setShowViewModal(true)
  }

  const handleViewSubscribers = (event: CalendarEvent) => {
    setSelectedEvent(event)
    setShowSubscribersModal(true)
  }

  const handleDeleteEvent = async (event: CalendarEvent) => {
    if (!confirm(`Are you sure you want to delete "${event.title}"?`)) {
      return
    }

    try {
      await deleteEvent(event.id)
      fetchEvents(currentPage, searchTerm, filterType, filterPriority, filterStatus)
    } catch (error: any) {
      console.error('Failed to delete event:', error)
      setError(error.message || 'Failed to delete event')
    }
  }

  const handleFormSubmit = async (payload: any) => {
    setIsSubmitting(true)
    try {
      if (selectedEvent) {
        await updateEvent(selectedEvent.id, payload)
      } else {
        await createEvent(payload)
      }
      setShowAddModal(false)
      setShowEditModal(false)
      setSelectedEvent(null)
      fetchEvents(currentPage, searchTerm, filterType, filterPriority, filterStatus)
    } catch (error: any) {
      console.error('Failed to save event:', error)
      setError(error.message || 'Failed to save event')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatTypeLabel = (type: string) => {
    if (type === 'college_event') return 'Institution Event'
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }

  return (
    <div className="gap-y-6 flex flex-col">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Calendar Management</h1>
        <button
          onClick={handleAddEvent}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 flex items-center space-x-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add Event</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1) }}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            >
              <option value="all">All Types</option>
              <option value="entrance_exam">Entrance Exam</option>
              <option value="application_deadline">Application Deadline</option>
              <option value="admission_result">Admission Result</option>
              <option value="counseling_session">Counseling Session</option>
              <option value="scholarship_deadline">Scholarship Deadline</option>
              <option value="career_fair">Career Fair</option>
              <option value="college_event">Institution Event</option>
              <option value="workshop">Workshop</option>
              <option value="seminar">Seminar</option>
              <option value="general">General</option>
            </select>

            <select
              value={filterPriority}
              onChange={(e) => { setFilterPriority(e.target.value); setCurrentPage(1) }}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as any); setCurrentPage(1) }}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <div className="text-red-800 text-sm">{error}</div>
          </div>
        </div>
      )}

      {/* Events Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : events.length === 0 ? (
          <div className="p-12 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterType !== 'all' || filterPriority !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first calendar event.'}
            </p>
            {(!searchTerm && filterType === 'all' && filterPriority === 'all' && filterStatus === 'all') && (
              <button
                onClick={handleAddEvent}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 inline-flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add First Event</span>
              </button>
            )}
          </div>
        ) : (
          <>
            <ul className="divide-y divide-gray-200 md:hidden">
              {events.map((event) => (
                <li key={event.id} className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-white" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
                      {event.description && (
                        <p className="text-xs text-gray-500 truncate">{event.description}</p>
                      )}
                    </div>
                    <span
                      className={cn(
                        'shrink-0 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full',
                        event.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
                      )}
                    >
                      {event.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
                    <span
                      className={cn(
                        'inline-flex px-2 py-0.5 font-semibold rounded-full',
                        typeColors[event.type] || 'bg-gray-100 text-gray-800',
                      )}
                    >
                      {formatTypeLabel(event.type)}
                    </span>
                    <span
                      className={cn(
                        'inline-flex px-2 py-0.5 font-semibold rounded-full capitalize',
                        priorityColors[event.priority] || 'bg-gray-100 text-gray-800',
                      )}
                    >
                      {event.priority}
                    </span>
                    <span
                      className={cn(
                        'inline-flex px-2 py-0.5 font-semibold rounded-full',
                        event.isGlobal ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800',
                      )}
                    >
                      {event.isGlobal ? 'Global' : 'Private'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3 text-gray-400" />
                      {formatEventDate(event.startDate)}
                    </span>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      onClick={() => handleViewEvent(event)}
                      className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
                      aria-label="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEditEvent(event)}
                      className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50"
                      aria-label="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleViewSubscribers(event)}
                      className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50"
                      aria-label="View Subscribers"
                    >
                      <Users className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteEvent(event)}
                      className="p-1.5 rounded-md text-red-600 hover:bg-red-50"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Event
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scope
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {events.map((event) => (
                    <tr key={event.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                              <Calendar className="h-5 w-5 text-white" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                              {event.title}
                            </div>
                            {event.description && (
                              <div className="text-sm text-gray-500 max-w-xs truncate">
                                {event.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn(
                          'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                          typeColors[event.type] || 'bg-gray-100 text-gray-800'
                        )}>
                          {formatTypeLabel(event.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn(
                          'inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize',
                          priorityColors[event.priority] || 'bg-gray-100 text-gray-800'
                        )}>
                          {event.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Clock className="h-3 w-3 mr-1 text-gray-400" />
                          {formatEventDate(event.startDate)}
                        </div>
                        {event.endDate && (
                          <div className="text-xs text-gray-500 ml-4">
                            to {formatEventDate(event.endDate)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn(
                          'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                          event.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        )}>
                          {event.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn(
                          'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                          event.isGlobal
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        )}>
                          {event.isGlobal ? 'Global' : 'Private'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewEvent(event)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditEvent(event)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleViewSubscribers(event)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="View Subscribers"
                          >
                            <Users className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEvent(event)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                total={totalEvents}
                pageSize={10}
                itemLabel="events"
              />
            </div>
          </>
        )}
      </div>

      {/* Add Event Modal */}
      <EventForm
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
      />

      {/* Edit Event Modal */}
      {selectedEvent && (
        <EventForm
          isOpen={showEditModal}
          onClose={() => { setShowEditModal(false); setSelectedEvent(null) }}
          onSubmit={handleFormSubmit}
          initialData={selectedEvent}
          isSubmitting={isSubmitting}
        />
      )}

      {/* View Event Modal */}
      {showViewModal && selectedEvent && (
        <EventViewModal
          isOpen={showViewModal}
          onClose={() => { setShowViewModal(false); setSelectedEvent(null) }}
          event={selectedEvent}
        />
      )}

      {/* Subscribers Modal */}
      {showSubscribersModal && selectedEvent && (
        <SubscribersModal
          isOpen={showSubscribersModal}
          onClose={() => { setShowSubscribersModal(false); setSelectedEvent(null) }}
          eventId={selectedEvent.id}
          eventTitle={selectedEvent.title}
        />
      )}
    </div>
  )
}

// Event View Modal Component
interface EventViewModalProps {
  isOpen: boolean
  onClose: () => void
  event: CalendarEvent
}

const EventViewModal: React.FC<EventViewModalProps> = ({
  isOpen,
  onClose,
  event
}) => {
  if (!isOpen) return null

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatTypeLabel = (type: string) => {
    if (type === 'college_event') return 'Institution Event'
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Event Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-indigo-500 rounded-lg flex items-center justify-center">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    event.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  )}>
                    {event.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    typeColors[event.type] || 'bg-gray-100 text-gray-800'
                  )}>
                    {formatTypeLabel(event.type)}
                  </span>
                  <span className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize',
                    priorityColors[event.priority] || 'bg-gray-100 text-gray-800'
                  )}>
                    {event.priority}
                  </span>
                </div>
              </div>
            </div>

            {event.description && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <p className="text-sm text-gray-900">{event.description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <p className="text-sm text-gray-900 flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-gray-400" />
                  {formatEventDate(event.startDate)}
                </p>
              </div>
              {event.endDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <p className="text-sm text-gray-900 flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-gray-400" />
                    {formatEventDate(event.endDate)}
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-gray-900">
                  {event.isGlobal ? 'Global' : 'Private'}
                </div>
                <div className="text-sm text-gray-500">Scope</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 capitalize">
                  {event.priority}
                </div>
                <div className="text-sm text-gray-500">Priority</div>
              </div>
            </div>

            {event.metadata && Object.keys(event.metadata).length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Metadata</label>
                <pre className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg overflow-x-auto">
                  {JSON.stringify(event.metadata, null, 2)}
                </pre>
              </div>
            )}

            <div className="text-sm text-gray-500 pt-4 border-t border-gray-200">
              <p>Created: {formatDate(event.createdAt)}</p>
              <p>Updated: {formatDate(event.updatedAt)}</p>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default CalendarManagement
